import { useEffect, useState } from 'react';
import config from '../config';
import { Wsi } from '../models/BackendModels';
import { auth_fetch, checkSuccess } from '../utils/ajax';

export const retrieveWsiMetadata = (wsiId: number): Promise<Wsi> => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    return auth_fetch(`${config.backend}/api/wsi/${wsiId}`, { signal: controller.signal })
        .then(checkSuccess)
        .then((data) => {
            const wsi = data[0];
            if (!wsi || wsi.id !== wsiId) {
                throw new Error('Image metadata response does not match the requested image.');
            }
            return wsi;
        })
        .finally(() => window.clearTimeout(timeout));
};

interface MetadataState {
    id?: number;
    wsi?: Wsi;
    settled: boolean;
}

/** Load metadata without coupling background refreshes to the cell queue. */
export default function useWsiMetadata(wsiId?: number, navigationKey?: number) {
    const [state, setState] = useState<MetadataState>({ settled: false });

    useEffect(() => {
        if (wsiId === undefined) {
            return;
        }
        let active = true;
        let request = 0;
        const refresh = () => {
            const currentRequest = ++request;
            retrieveWsiMetadata(wsiId)
                .then((wsi) => {
                    if (active && currentRequest === request) {
                        setState({ id: wsiId, wsi, settled: true });
                    }
                })
                .catch(() => {
                    if (active && currentRequest === request) {
                        // A failed refresh retains only this image's successful metadata.
                        setState((previous) => ({
                            id: wsiId,
                            wsi: previous.id === wsiId ? previous.wsi : undefined,
                            settled: true,
                        }));
                    }
                });
        };
        refresh();
        const interval = window.setInterval(refresh, 60_000);
        window.addEventListener('focus', refresh);
        return () => {
            active = false;
            window.clearInterval(interval);
            window.removeEventListener('focus', refresh);
        };
    }, [wsiId, navigationKey]);

    // Guard during render, before effects run, so an image switch never shows old context.
    const matches = state.id === wsiId;
    return {
        wsi: matches ? state.wsi : undefined,
        loading: wsiId !== undefined && (!matches || !state.settled),
    };
}
